import React, { useEffect, useRef } from 'react';
import { getMinMaxPairs } from './waveformUtils';

interface WaveformPlotProps {
  analyser: AnalyserNode | null;
  width?: number; // optional initial/fallback width; canvas will fill container width when available
  height?: number;
  fps?: number; // target frames per second (approx)
  status?: 'idle' | 'loading' | 'playing' | 'paused';
  className?: string;
  color?: string; // waveform stroke color
  background?: string; // background color
  showGrid?: boolean; // draw background grid
  gridColor?: string; // grid line color
  gridHorizLines?: number; // number of horizontal divisions (excluding borders)
  gridVertLines?: number; // number of vertical divisions (excluding borders)
}

/**
 * WaveformPlot
 * Renders a real-time min-max decimated waveform from an AnalyserNode's time domain data.
 * - Updates at ~fps (default 30) using rAF throttling.
 * - Freezes when status === 'paused'.
 * - Clears when status === 'idle'.
 */
const WaveformPlot: React.FC<WaveformPlotProps> = ({
  analyser,
  width = 600,
  height = 120,
  fps = 30,
  status = 'playing',
  className = '',
  color = '#7fffd4',
  // Use a Tailwind-like CSS variable if available, fallback to requested dark background
  background = 'var(--neutral-950, #0b0f19)',
  showGrid = true,
  gridColor = 'rgba(255,255,255,0.06)',
  gridHorizLines = 4,
  gridVertLines = 8,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawWRef = useRef<number>(width);
  const drawHRef = useRef<number>(height);
  const lastFrameRef = useRef(0);
  const dataRef = useRef<Float32Array | null>(null);
  const rafRef = useRef<number | null>(null);

  // Size canvas to container width (responsive) with devicePixelRatio scaling
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ro = ('ResizeObserver' in window) ? new ResizeObserver(() => sizeCanvas()) : null;
    if (ro) ro.observe(c.parentElement ?? c);
    window.addEventListener('resize', sizeCanvas);
    // initial
    sizeCanvas();
    return () => {
      window.removeEventListener('resize', sizeCanvas);
      try { ro?.disconnect(); } catch {}
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  function sizeCanvas() {
    const c = canvasRef.current;
    if (!c) return;
    const parent = c.parentElement;
    const cssW = Math.max(1, Math.floor((parent?.clientWidth || c.clientWidth || width)));
    const cssH = height;
    const dpr = Math.max(1, Math.floor((window as any).devicePixelRatio || 1));
    drawWRef.current = cssW;
    drawHRef.current = cssH;
    // Set CSS size
    c.style.width = '100%';
    c.style.height = cssH + 'px';
    // Set buffer size
    const bufW = Math.max(1, Math.floor(cssW * dpr));
    const bufH = Math.max(1, Math.floor(cssH * dpr));
    if (c.width !== bufW) c.width = bufW;
    if (c.height !== bufH) c.height = bufH;
    const ctx = c.getContext('2d');
    if (ctx) {
      // Scale drawing so we can use CSS pixel coordinates
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
  }

  // Main animation loop
  useEffect(() => {
    function drawFrame(ts: number) {
      rafRef.current = requestAnimationFrame(drawFrame);
      const c = canvasRef.current;
      if (!c) return;
  const ctx = c.getContext('2d');
      if (!ctx) return;

      const clear = () => {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, drawWRef.current, drawHRef.current);
      };

      if (!analyser) { clear(); return; }

      const interval = 1000 / fps;
      if (ts - lastFrameRef.current < interval) return; // throttle
      lastFrameRef.current = ts;

      if (status === 'idle') { clear(); return; }

      if (status === 'paused') {
        if (dataRef.current) {
          renderSamples(ctx, dataRef.current, drawWRef.current, drawHRef.current, color, background, { showGrid, gridColor, gridHorizLines, gridVertLines });
        } else {
          // still draw background & grid if no data yet
          renderSamples(ctx, new Float32Array(0), drawWRef.current, drawHRef.current, color, background, { showGrid, gridColor, gridHorizLines, gridVertLines, skipWaveform: true });
        }
        return;
      }

      // playing/loading
      const fftSize = analyser.fftSize;
      if (!dataRef.current || dataRef.current.length !== fftSize) {
        dataRef.current = new Float32Array(fftSize);
      }
      // Cast to any to satisfy potential lib mismatch
      (analyser as any).getFloatTimeDomainData(dataRef.current);
  renderSamples(ctx, dataRef.current!, drawWRef.current, drawHRef.current, color, background, { showGrid, gridColor, gridHorizLines, gridVertLines });
    }

    rafRef.current = requestAnimationFrame(drawFrame);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [analyser, fps, height, width, color, background, status]);

  // Clear immediately if idle & analyser changes
  useEffect(() => {
    if (status !== 'idle') return;
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (c && ctx) {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, drawWRef.current, drawHRef.current);
    }
  }, [status, background]);

  return (
    <canvas
      ref={canvasRef}
      className={"block select-none rounded " + className}
      style={{ width: '100%', height: height + 'px' }}
    />
  );
};

interface GridOpts { showGrid?: boolean; gridColor?: string; gridHorizLines?: number; gridVertLines?: number; skipWaveform?: boolean }
function renderSamples(ctx: CanvasRenderingContext2D, samples: Float32Array, width: number, height: number, color: string, background: string, grid?: GridOpts) {
  if (background !== 'transparent') {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, width, height);
  } else {
    // Clear with full transparency
    ctx.clearRect(0, 0, width, height);
  }

  // Grid (draw before waveform)
  if (grid?.showGrid) {
    ctx.save();
    ctx.strokeStyle = grid.gridColor || 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 1;
    const hLines = Math.max(0, grid.gridHorizLines ?? 4);
    for (let i = 1; i <= hLines; i++) {
      const y = (i / (hLines + 1)) * height + 0.5;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
    }
    const vLines = Math.max(0, grid.gridVertLines ?? 8);
    for (let i = 1; i <= vLines; i++) {
      const x = (i / (vLines + 1)) * width + 0.5;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
    }
    ctx.restore();
  }

  if (grid?.skipWaveform) return;

  const pairs = samples.length ? getMinMaxPairs(samples, width) : [];
  const midY = height / 2;
  const amp = height / 2; // scale [-1,1] to canvas

  // Gradient stroke for harmony with other visualizations (subtle aqua to purple)
  const grad = ctx.createLinearGradient(0, 0, width, 0);
  grad.addColorStop(0, '#22c55e');
  grad.addColorStop(0.6, '#22c55e');
  grad.addColorStop(1, '#22c55e');

  ctx.lineWidth = 1;
  ctx.strokeStyle = grad;
  ctx.beginPath();
  const len = pairs.length;
  for (let x = 0; x < len; x++) {
    const { min, max } = pairs[x];
    const y1 = midY + min * amp;
    const y2 = midY + max * amp;
    // Use exact x pixel (no +0.5) so we cover final edge, then center via crispEdges tweak:
    ctx.moveTo(x, y1);
    ctx.lineTo(x, y2);
  }
  ctx.stroke();

  // Subtle glow overlay
  ctx.globalCompositeOperation = 'lighter';
  ctx.strokeStyle = 'rgba(143,255,198,0.08)';
  ctx.beginPath();
  for (let x = 0; x < len; x += 2) { // fewer strokes for perf
    const { min, max } = pairs[x];
    const y1 = midY + min * amp;
    const y2 = midY + max * amp;
    ctx.moveTo(x, y1);
    ctx.lineTo(x, y2);
  }
  ctx.stroke();
  ctx.globalCompositeOperation = 'source-over';
}

export default WaveformPlot;
